import { SHOW_ROMSH_INPUTS } from "@/config/displayPolicy";
import { ROMSH_INPUT_EVIDENCE_TEXT } from "@/constants/romshInputText";
import type { AnalysisEvidence } from "@/types";
import { romshInputEvidenceLine } from "@/utils/romshInput";

/**
 * EvidenceCard — T2-2 불변 증거 스냅숏.
 *
 * 분석 완료 시점에 고정된 입력 해시(원파일·파싱 스캔)·전처리·파라미터 실제
 * 사용값·엔진 버전·결과 해시를 보여준다. 배포 후 코드·DB 가 바뀌어도 과거
 * 분석을 당시 상태로 대조하기 위한 기록이다 (기획 v4 §3-②).
 *
 * 스냅숏이 없는 분석(도입 2026-08-20 이전)은 "기록 없음"을 말한다 —
 * 오류가 아니라 사실이고, 소급 기입하지 않는다.
 */

interface EvidenceCardProps {
  evidence: AnalysisEvidence | null | undefined;
}

function Hash({ value }: { value: string | null | undefined }) {
  if (!value) {
    return <span className="text-gray-400">없음</span>;
  }
  return (
    <span className="font-mono text-xs text-gray-800" title={value}>
      {value.slice(0, 16)}…
    </span>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 py-1">
      <dt className="w-40 shrink-0 text-xs font-medium text-gray-500">
        {label}
      </dt>
      <dd className="text-xs text-gray-800">{children}</dd>
    </div>
  );
}

export default function EvidenceCard({ evidence }: EvidenceCardProps) {
  if (!evidence) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          불변 증거 스냅숏
        </h2>
        <p className="text-sm text-gray-500">
          증거 스냅숏 없음 — 스냅숏 도입(2026-08-20) 이전 분석입니다. 기록이
          없다는 사실 그대로이며, 소급 기입하지 않습니다.
        </p>
      </div>
    );
  }

  // PLAN 2-9a: RoMSH 입력 기록 한 줄 — 기록만으로 판별(§3-3 끝 표)
  const romshInputLine = SHOW_ROMSH_INPUTS
    ? romshInputEvidenceLine(evidence)
    : null;
  const engines = evidence.engine?.identification_engine;
  const engineLabel = Array.isArray(engines) ? engines.join(" · ") : engines;

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">
        불변 증거 스냅숏
      </h2>
      <p className="text-xs text-gray-400 mb-3">
        분석 완료 시점에 1회 기록 — 수정되지 않습니다. 과거 분석을 당시 상태로
        대조하는 근거입니다.
      </p>
      <dl className="divide-y divide-gray-100">
        <Row label="시료 원파일 SHA-256">
          <Hash value={evidence.source_file_sha256} />
        </Row>
        <Row label="MS 입력(파싱 스캔) SHA-256">
          <Hash value={evidence.ms_data_sha256} />
        </Row>
        {evidence.spectrum_data_sha256 && (
          <Row label="단일 스펙트럼 입력 SHA-256">
            <Hash value={evidence.spectrum_data_sha256} />
          </Row>
        )}
        <Row label="전처리">
          {evidence.preprocessing?.background_mz
            ? `배경가스 m/z 제외: ${evidence.preprocessing.background_mz.join(", ")}`
            : "기록 없음"}
        </Row>
        <Row label="동정 파라미터">
          {evidence.parameters
            ? `${evidence.parameters.per_scan ? "스캔별 동정" : "단일 호출"} · top_k ${evidence.parameters.top_k} · min_match_score ${evidence.parameters.min_match_score}`
            : "기록 없음"}
        </Row>
        <Row label="엔진 · 모델">
          {engineLabel ?? "—"} · {evidence.engine?.model_version ?? "—"}
          {evidence.engine?.database_sources?.length
            ? ` · ${evidence.engine.database_sources.join(", ")}`
            : ""}
          {evidence.engine?.romsh_model_version
            ? ` · RoMSH ${evidence.engine.romsh_model_version}`
            : ""}
        </Row>
        {romshInputLine !== null && (
          <Row label={ROMSH_INPUT_EVIDENCE_TEXT.label}>
            <span data-testid="romsh-input-evidence-line">
              {romshInputLine}
            </span>
          </Row>
        )}
        <Row label={`결과 해시 (${evidence.result_hash_version ?? "v1"})`}>
          <Hash value={evidence.result_hash} />
        </Row>
        {evidence.created_at && (
          <Row label="기록 시각">
            {new Date(evidence.created_at).toLocaleString("ko-KR")}
          </Row>
        )}
      </dl>
    </div>
  );
}
